<template>
  <div>
    <b-spinner v-if="loading" />
    <div v-else>
      <b-alert :show="bothMissing" variant="warning" class="mb-3">
        {{ $t('partner.tax.both-missing-warning') }}
      </b-alert>

      <b-form-group
        :label="$t('partner.tax.tax-id-label')"
        label-for="partner-tax-id-input"
        :description="$t('partner.tax.tax-id-hint')"
      >
        <b-form-input
          id="partner-tax-id-input"
          v-model="taxId"
          :placeholder="$t('partner.tax.tax-id-placeholder')"
          :disabled="saving"
          maxlength="25"
        />
      </b-form-group>

      <b-form-group
        :label="$t('partner.tax.vat-id-label')"
        label-for="partner-vat-id-input"
        :description="$t('partner.tax.vat-id-hint')"
      >
        <b-form-input
          id="partner-vat-id-input"
          v-model="vatId"
          :placeholder="$t('partner.tax.vat-id-placeholder')"
          :disabled="saving"
          maxlength="255"
        />
      </b-form-group>

      <div class="d-flex justify-content-end mt-3">
        <b-button
          variant="secondary"
          class="mr-2"
          :disabled="saving || !hasChanges"
          @click="cancelChanges"
        >
          {{ $t('partner.tax.cancel') }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="saving || !hasChanges"
          @click="save"
        >
          <b-spinner small v-if="saving" class="mr-1" />
          {{ $t('partner.tax.save') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapMutations, mapGetters } from 'vuex';
import { UPDATE_PARTNER_ACCOUNT_FIELDS } from '@/store/partners.module';

export default {
  name: 'PartnerBillingTax',
  props: {
    partner: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      taxId: '',
      vatId: '',
      originalTaxId: '',
      originalVatId: '',
      loading: false,
      saving: false,
    };
  },
  computed: {
    ...mapGetters(['getPartnerByAccountId']),
    hasChanges() {
      return (
        this.taxId !== this.originalTaxId || this.vatId !== this.originalVatId
      );
    },
    bothMissing() {
      return !this.taxId?.trim() && !this.vatId?.trim();
    },
  },
  watch: {
    partner: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    ...mapMutations([UPDATE_PARTNER_ACCOUNT_FIELDS]),

    load() {
      if (!this.partner?.account?.id) {
        return;
      }

      this.loading = true;

      // Prefer the store value (reflects edits made this session) over the prop.
      const storePartner = this.getPartnerByAccountId(this.partner.account.id);
      const account = storePartner?.account ?? this.partner.account;

      this.taxId = account.taxId || '';
      this.vatId = account.vatId || '';
      this.originalTaxId = this.taxId;
      this.originalVatId = this.vatId;
      this.loading = false;
    },

    async save() {
      if (!this.partner?.account?.id) {
        this.$bvToast.toast(this.$t('partner.tax.error.no-account'), {
          title: this.$t('partner.tax.error.title'),
          variant: 'danger',
        });
        return;
      }

      this.saving = true;

      const payload = {
        taxId: this.taxId?.trim() || null,
        vatId: this.vatId?.trim() || null,
      };

      try {
        await axios.patch(
          `/data-api/account/${this.partner.account.id}`,
          payload,
        );

        this[UPDATE_PARTNER_ACCOUNT_FIELDS]({
          accountId: this.partner.account.id,
          fields: payload,
        });

        this.originalTaxId = this.taxId;
        this.originalVatId = this.vatId;
        this.$bvToast.toast(this.$t('partner.tax.success'), {
          title: this.$t('partner.tax.success-title'),
          variant: 'success',
        });
      } catch (error) {
        console.error('Error saving tax/VAT info:', error);
        this.$bvToast.toast(this.$t('partner.tax.error.save'), {
          title: this.$t('partner.tax.error.title'),
          variant: 'danger',
        });
      } finally {
        this.saving = false;
      }
    },

    cancelChanges() {
      this.taxId = this.originalTaxId;
      this.vatId = this.originalVatId;
    },
  },
};
</script>
